/**
 * kör-69 #19 (2a): Gépkategória-admin — lista + létrehozás/szerkesztés.
 *
 * Az `#1828` (RLS) + `#1848` (POST/PATCH) után a `MachineCategory` tábla
 * feltölthető. Ez az oldal a `CategoryHourlyRateConfigPage` layout-mintáját
 * követi (közös `/settings` fejléc + Card + tábla), nem talál ki újat.
 *
 * ⛔ SZÁNDÉKOSAN NEM RÉSZE (🚦 ruling 2026-09-07, `(A)` vágás): a wizard-store,
 * a kategória-picker és a bevizsgálási díj. Azok a `2b`-ben mozdulnak, együtt,
 * mert a store `machineCategoryId` mezője ma `code`-ot tárol, és az `id`-re
 * állás önmagában NÉMÁN 5000 Ft-ra vinné a díjat (`INSPECTION_FEE_TABLE[id]`
 * → `undefined` → `?? INSPECTION_FEE_DEFAULT`).
 *
 * ⚠️ Ezért az itt LÉTREHOZOTT kategória a munkalap-varázslóban még nem
 * választható — ezt az oldal ki is írja (`newCategoryNotInWizard`), hogy a
 * hiány látható legyen, ne néma.
 */

import {
  createMachineCategory,
  getMachineCategories,
  type MachineCategory,
  updateMachineCategory,
} from '@/api/worksheets';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useApiError } from '@/hooks/use-api-error';
import { useTranslation } from '@kgc/i18n';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface FormState {
  code: string;
  name: string;
  sortOrder: string;
}

const EMPTY_FORM: FormState = { code: '', name: '', sortOrder: '' };

export function MachineCategoryAdminPage() {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const navigate = useNavigate();

  const [categories, setCategories] = useState<MachineCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMachineCategories();
      setCategories(res.data);
    } catch {
      setError(t('common.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const startCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setError(null);
  };

  const startEdit = (cat: MachineCategory) => {
    setEditingId(cat.id);
    setForm({ code: cat.code, name: cat.name, sortOrder: String(cat.sortOrder) });
    setError(null);
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    // A `sortOrder` csak akkor megy ki, ha a mező nem üres — üres PATCH-re a BE
    // `MACHINE_CATEGORY_EMPTY_PATCH`-et ad, és a `0` érvényes érték.
    const sortOrder = form.sortOrder.trim() === '' ? undefined : Number(form.sortOrder);
    try {
      if (editingId === null) {
        await createMachineCategory({ code: form.code.trim(), name: form.name.trim(), sortOrder });
      } else {
        await updateMachineCategory(editingId, {
          code: form.code.trim(),
          name: form.name.trim(),
          sortOrder,
        });
      }
      setForm(EMPTY_FORM);
      setEditingId(null);
      await loadData();
    } catch (err) {
      // Kód-alapú fordítás (`error.MACHINE_CATEGORY_CODE_TAKEN` stb.) — a
      // `use-api-error` az `err.code`-ot nézi, nem a HTTP-státuszt.
      setError(getErrorMessage(err, t('common.error')));
    } finally {
      setSaving(false);
    }
  };

  const canSubmit = form.code.trim().length > 0 && form.name.trim().length > 0 && !saving;

  if (loading) {
    return (
      <div className="min-h-screen kgc-bg p-6">
        <div className="max-w-4xl mx-auto">
          <Card>
            <CardContent className="p-12 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4" />
              <p className="text-muted-foreground">{t('common.loading')}</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen kgc-bg">
      <div className="bg-white dark:bg-[#1a4f5c] border-b border-gray-200 dark:border-[#3a9aa8] px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate('/settings')}>
            {t('common.back')}
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {t('worksheet.machineCategoryAdmin.title')}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t('worksheet.machineCategoryAdmin.subtitle')}
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6">
        {error && (
          <div
            className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-300 dark:border-red-600 text-red-700 dark:text-red-300 text-sm"
            data-testid="category-admin-error"
          >
            {error}
          </div>
        )}

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>
              {editingId === null
                ? t('worksheet.machineCategoryAdmin.createTitle')
                : t('worksheet.machineCategoryAdmin.editTitle')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-3">
              <label className="text-sm">
                <span className="text-muted-foreground">
                  {t('worksheet.machineCategoryAdmin.code')}
                </span>
                <Input
                  value={form.code}
                  data-testid="category-code-input"
                  onChange={e => setForm(prev => ({ ...prev, code: e.target.value }))}
                />
              </label>
              <label className="text-sm">
                <span className="text-muted-foreground">
                  {t('worksheet.machineCategoryAdmin.name')}
                </span>
                <Input
                  value={form.name}
                  data-testid="category-name-input"
                  onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                />
              </label>
              <label className="text-sm">
                <span className="text-muted-foreground">
                  {t('worksheet.machineCategoryAdmin.sortOrder')}
                </span>
                <Input
                  type="number"
                  value={form.sortOrder}
                  data-testid="category-sortorder-input"
                  onChange={e => setForm(prev => ({ ...prev, sortOrder: e.target.value }))}
                />
              </label>
            </div>

            <div className="mt-4 flex gap-2">
              <Button disabled={!canSubmit} data-testid="category-submit" onClick={handleSubmit}>
                {saving ? '...' : t('common.save')}
              </Button>
              {editingId !== null && (
                <Button variant="outline" onClick={startCreate}>
                  {t('common.cancel')}
                </Button>
              )}
            </div>

            <div className="mt-4 p-3 rounded bg-amber-50 dark:bg-amber-950/20 text-sm text-amber-800 dark:text-amber-300">
              {t('worksheet.machineCategoryAdmin.newCategoryNotInWizard')}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('worksheet.machineCategoryAdmin.tableTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                      {t('worksheet.machineCategoryAdmin.code')}
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                      {t('worksheet.machineCategoryAdmin.name')}
                    </th>
                    <th className="text-right py-3 px-4 font-medium text-muted-foreground">
                      {t('worksheet.machineCategoryAdmin.sortOrder')}
                    </th>
                    <th className="text-center py-3 px-4 font-medium text-muted-foreground">
                      {t('common.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map(cat => (
                    <tr
                      key={cat.id}
                      className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-900/20"
                    >
                      <td className="py-3 px-4 font-mono text-xs">{cat.code}</td>
                      <td className="py-3 px-4 font-medium">{cat.name}</td>
                      <td className="py-3 px-4 text-right text-muted-foreground">
                        {cat.sortOrder}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <Button size="sm" variant="outline" onClick={() => startEdit(cat)}>
                          {t('common.edit')}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
